import { Box, Grid, TextField, Typography } from '@mui/material';
import React from 'react';
import { Control, FieldValues } from 'react-hook-form';
import {
	AutocompleteFieldConfiguration,
	CurrencyFieldConfiguration,
	DateFieldConfiguration,
	EmailFieldConfiguration,
	FieldConfiguration,
	FrontendExternalDataCallConfiguration,
	GroupFieldConfiguration,
	LookupFieldConfiguration,
	MediaFieldConfiguration,
	NumberFieldConfiguration,
	SelectFieldConfiguration,
	StringFieldConfiguration,
} from '../../../api/ConfigApi';
import { AuthData } from '../../../context/auth';
import { errorToast, successToast } from '../../Toast';
import { FIELD_TYPES, FORM_MESSAGE } from '../data/constants';
import {
	CurrencyField,
	DateField,
	EmailField,
	GroupField,
	LookupField,
	MediaField,
	MultiSelectField,
	NumberField,
	PolicyholderLookupField,
	PolicyLookupField,
	SelectField,
	StringField,
	WatchWrapper,
} from '../fields';
import { Conditions, DefaultValuesType } from '../types';

/**
 * @param {string} form - The form name to render, the key of FormShape
 * @param {FieldConfiguration[]} fields - Fields from policy config
 * @param {FrontendExternalDataCallConfiguration[]} externalDataCalls - External data calls from policy config
 * @param {Control} control - react-hook-form control
 * @param {AuthData} authData - Auth data from context
 * @param {string} productName - product context
 * @param {string} configVersion - Config version
 * @param {DefaultValuesType} defaultValues - Default values, generated from a formBuilder
 * @param {string} [groupFieldName] - Optional, the name of the group field used for recursive building
 * @param {boolean} [disabled] - Optional, if the form is disabled (or locked)
 * @param {SaveFastForm} [save] - Optional, the save function
 * @return {*} - Renders the Fast Form
 */
const Form = (
	form: string,
	fields: FieldConfiguration[],
	externalDataCalls: FrontendExternalDataCallConfiguration[],
	control: Control,
	authData: AuthData,
	productName: string,
	configVersion: string,
	defaultValues: DefaultValuesType,
	groupFieldName?: string,
	disabled?: boolean,
	effectiveDate?: string,
	save?: () => void,
	setLoading?: (loading: boolean) => void,
) => {
	const commonFieldProps = { disabled };
	return fields.map((field) => {
		const fieldName = groupFieldName
			? form + '.' + groupFieldName + '.' + field.name
			: form + '.' + field.name;

		const fieldHasExternalDataCalls = externalDataCalls.some(
			(externalDataCall) => externalDataCall.triggerField === field.name,
		);

		const saveForm = async (currValue: string, fieldName: string) => {
			const savedValue = control.getFieldState(fieldName);
			if (currValue !== '' && savedValue.isDirty) {
				await setLoading(true);
				try {
					await save();
					successToast('Successfully updated policy');
				} catch (error) {
					errorToast('Error updating policy');
				} finally {
					await setLoading(false);
				}
			}
		};

		let controlledField = null as JSX.Element;
		switch (field.type) {
			case FIELD_TYPES.string:
				const stringField = {
					...field,
					...commonFieldProps,
				} as StringFieldConfiguration;
				controlledField = (
					<StringField
						control={control}
						save={fieldHasExternalDataCalls ? saveForm : null}
						{...stringField}
						name={fieldName}
					/>
				);
				break;
			case FIELD_TYPES.select:
				const selectField = {
					...field,
					...commonFieldProps,
				} as SelectFieldConfiguration;
				const muliSelectField = selectField.repeatable;
				if (muliSelectField) {
					controlledField = (
						<MultiSelectField
							control={control}
							save={fieldHasExternalDataCalls ? saveForm : null}
							{...selectField}
							name={fieldName}
							fieldHasExternalDataCalls={fieldHasExternalDataCalls}
						/>
					);
				} else {
					controlledField = (
						<SelectField
							save={fieldHasExternalDataCalls ? saveForm : null}
							control={control}
							{...selectField}
							name={fieldName}
							fieldHasExternalDataCalls={fieldHasExternalDataCalls}
						/>
					);
				}
				break;
			case FIELD_TYPES.date:
				const dateField = {
					...field,
					...commonFieldProps,
				} as DateFieldConfiguration;
				controlledField = (
					<DateField
						control={control}
						authData={authData}
						save={fieldHasExternalDataCalls ? saveForm : null}
						{...dateField}
						name={fieldName}
					/>
				);
				break;
			case FIELD_TYPES.currency:
				const currencyField = {
					...field,
					...commonFieldProps,
				} as CurrencyFieldConfiguration;
				controlledField = (
					<CurrencyField
						control={control}
						save={fieldHasExternalDataCalls ? saveForm : null}
						{...currencyField}
						currency={currencyField.currency}
						name={fieldName}
					/>
				);
				break;
			case FIELD_TYPES.policyLookup:
				const policyLookupField = {
					...field,
					...commonFieldProps,
				} as AutocompleteFieldConfiguration;
				controlledField = (
					<PolicyLookupField
						control={control}
						{...policyLookupField}
						name={fieldName}
					/>
				);
				break;
			case FIELD_TYPES.policyholderLookup:
				const policyholderLookupField = {
					...field,
					...commonFieldProps,
				} as AutocompleteFieldConfiguration;
				controlledField = (
					<PolicyholderLookupField
						control={control}
						{...policyholderLookupField}
						name={fieldName}
					/>
				);
				break;
			case FIELD_TYPES.email:
				const emailField = {
					...field,
					...commonFieldProps,
				} as EmailFieldConfiguration;
				controlledField = (
					<EmailField
						save={fieldHasExternalDataCalls ? saveForm : null}
						control={control}
						{...emailField}
						name={fieldName}
					/>
				);
				break;
			case FIELD_TYPES.number:
				const numberField = {
					...field,
					...commonFieldProps,
				} as NumberFieldConfiguration;
				controlledField = (
					<NumberField
						save={fieldHasExternalDataCalls ? saveForm : null}
						control={control}
						{...numberField}
						name={fieldName}
					/>
				);
				break;
			case FIELD_TYPES.lookup:
				const lookupField = {
					...field,
					...commonFieldProps,
				} as LookupFieldConfiguration;
				controlledField = (
					<LookupField
						form={form}
						control={control}
						authData={authData}
						configVersion={configVersion}
						productName={productName}
						save={fieldHasExternalDataCalls ? saveForm : null}
						{...lookupField}
						name={fieldName}
						fieldHasExternalDataCalls={fieldHasExternalDataCalls}
						groupFieldName={groupFieldName}
						effectiveDate={effectiveDate}
					/>
				);
				break;
			case FIELD_TYPES.group:
				const groupField = {
					...field,
					...commonFieldProps,
				} as GroupFieldConfiguration;
				controlledField = (
					<GroupField
						form={form}
						groupField={groupField}
						control={control}
						authData={authData}
						productName={productName}
						configVersion={configVersion}
						defaultValues={defaultValues}
						effectiveDate={effectiveDate}
					/>
				);
				break;
			case FIELD_TYPES.media:
				const mediaField = {
					...field,
					...commonFieldProps,
				} as MediaFieldConfiguration;
				controlledField = (
					<MediaField
						authData={authData}
						control={control}
						{...mediaField}
						name={fieldName}
					/>
				);
				break;
			default:
				controlledField = (
					<TextField
						variant='outlined'
						fullWidth
						disabled
						label={field.title}
						defaultValue={FORM_MESSAGE.notImplemented}
					/>
				);
		}
		let fieldWidth = field.width
			? parseInt('' + field.width, 10)
			: field.type === FIELD_TYPES.group
			? 12
			: 6;

		controlledField = (
			<Grid key={field.name + '-' + field.order} item xs={12} sm={fieldWidth}>
				{controlledField}
			</Grid>
		);

		if (field.heading) {
			controlledField = (
				<>
					<Grid xs={12} item>
						<Typography>{field.heading}</Typography>
					</Grid>
					{controlledField}
				</>
			);
		}

		if (field.newline) {
			controlledField = (
				<>
					<Box width={1} />
					{controlledField}
				</>
			);
		}

		if (field.condition) {
			let prefix = groupFieldName ? `${form}.${groupFieldName}.` : `${form}.`;
			return (
				<WatchWrapper
					key={fieldName}
					form={form}
					control={control}
					condition={
						Object.fromEntries(
							Object.entries(field.condition).map(([k, v]) => [
								`${prefix}${k}`,
								v,
							]),
						) as Conditions<FieldValues>
					}
				>
					{controlledField}
				</WatchWrapper>
			);
		}

		return <React.Fragment key={fieldName}>{controlledField}</React.Fragment>;
	});
};

export default Form;
